/**
 * IssueRepairModal.jsx
 *
 * Auto-opens after chapter analysis. Shows the full assessment:
 *   • Continuity / Pacing / Plotholes / Clichés score header
 *   • Issue list on the left rail (with per-issue Fix →)
 *   • Chapter text editor on the right
 *   • Stay-in-modal Save + Re-run: re-runs analysis, refreshes scores inline
 *   • Claude auto-fix with scope: "This Chapter" or "Full Manuscript"
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertTriangle, CheckCircle2, Sparkles, Save, RefreshCw,
  Loader2, BookOpen, TrendingUp, TrendingDown, Minus,
  BookCheck, Gauge, Activity,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { issueRepairApi, generateApi } from '@/lib/api';

// ── Tiny helpers ──────────────────────────────────────────────────────────────

const scoreTone = (key, val) => {
  if (val == null) return 'muted';
  if (key === 'continuity') return val >= 85 ? 'success' : val >= 60 ? 'warning' : 'destructive';
  if (key === 'pacing')     return val >= 70 ? 'success' : val >= 45 ? 'warning' : 'destructive';
  if (key === 'plotholes')  return val === 0  ? 'success' : 'warning';
  if (key === 'cliches')    return val <= 3   ? 'success' : 'warning';
  return 'muted';
};

const toneClass = (tone) => ({
  success:     'text-emerald-400',
  warning:     'text-amber-400',
  destructive: 'text-rose-400',
  muted:       'text-muted-foreground',
})[tone] || 'text-muted-foreground';

const delta = (next, prev) => {
  if (next == null || prev == null) return null;
  const d = next - prev;
  if (Math.abs(d) < 0.5) return null;
  return d;
};

// ── Score pill ────────────────────────────────────────────────────────────────
function ScorePill({ label, value, prevValue, suffix = '%' }) {
  const d = delta(value, prevValue);
  const tone = scoreTone(label.toLowerCase().replace(/ /g, ''), value);
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={cn('text-[17px] font-bold leading-none', toneClass(tone))}>
        {value ?? '—'}{value != null ? suffix : ''}
      </span>
      {d != null && (
        <span className={cn('flex items-center gap-0.5 text-[9.5px] font-semibold',
          d > 0 ? 'text-emerald-400' : 'text-rose-400')}>
          {d > 0
            ? <TrendingUp className="h-2.5 w-2.5" />
            : <TrendingDown className="h-2.5 w-2.5" />}
          {d > 0 ? '+' : ''}{d.toFixed(0)}
        </span>
      )}
    </div>
  );
}

// ── Scope toggle ──────────────────────────────────────────────────────────────
function ScopeToggle({ value, onChange }) {
  return (
    <div className="flex rounded-md border border-border overflow-hidden text-[11px] font-medium">
      {['chapter', 'manuscript'].map(s => (
        <button key={s} type="button" onClick={() => onChange(s)}
          className={cn('flex-1 px-2 py-1 transition-colors',
            value === s
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-muted-foreground hover:bg-secondary')}>
          {s === 'chapter' ? 'This Chapter' : 'Full Manuscript'}
        </button>
      ))}
    </div>
  );
}

// ── Issue row ─────────────────────────────────────────────────────────────────
function IssueRow({ issue, active, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid="issue-repair-row"
      className={cn(
        'w-full text-left flex items-start gap-2 px-3 py-2.5 transition-colors border-b border-border last:border-0',
        active ? 'bg-secondary/70' : 'hover:bg-secondary/40',
      )}>
      {issue.tone === 'success'
        ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
        : <AlertTriangle className={cn('mt-0.5 h-3.5 w-3.5 shrink-0',
            issue.tone === 'destructive' ? 'text-rose-400' : 'text-amber-400')} />}
      <span className="text-[12px] text-foreground/90 line-clamp-2">{issue.text}</span>
      {issue.chapter_number && (
        <Badge variant="outline" className="ml-auto shrink-0 text-[10px]">
          Ch {issue.chapter_number}
        </Badge>
      )}
    </button>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export function IssueRepairModal({
  open,
  onOpenChange,
  issues = [],
  scores = null,          // { continuity, pacing, plotholes, cliches } from last analysis
  projectId,
  initialChapterId,
  chapterContent = '',
  onRepairAccepted,
  onRerunAnalysis,        // (chapterId) => Promise<analysisResult>  — parent provides
}) {
  const [selectedIdx,    setSelectedIdx]   = useState(0);
  const [editText,       setEditText]      = useState('');
  const [claudeScope,    setClaudeScope]   = useState('chapter');
  const [saving,         setSaving]        = useState(false);
  const [claudeState,    setClaudeState]   = useState('idle');
  const [repairs,        setRepairs]       = useState([]);
  const [activeRepairId, setActiveRepairId] = useState(null);
  const [patchPreview,   setPatchPreview]  = useState(null);
  const [runningRerun,   setRunningRerun]  = useState(false);
  const [liveScores,     setLiveScores]    = useState(null);  // updated after re-run
  const [prevScores,     setPrevScores]    = useState(null);  // snapshot before re-run
  const [liveIssues,     setLiveIssues]    = useState(null);  // updated after re-run
  const didSeedRef = useRef(false);

  // Seed editor text once per open session
  useEffect(() => {
    if (open && !didSeedRef.current) {
      setEditText(chapterContent);
      setLiveScores(null);
      setPrevScores(null);
      setLiveIssues(null);
      setClaudeState('idle');
      setPatchPreview(null);
      setRepairs([]);
      setActiveRepairId(null);
      didSeedRef.current = true;
      if (initialChapterId && issues.length) {
        const idx = issues.findIndex(i => i.chapter_id === initialChapterId);
        if (idx >= 0) setSelectedIdx(idx);
      }
    }
    if (!open) didSeedRef.current = false;
  }, [open]);

  const displayScores = liveScores || scores;
  const displayIssues = liveIssues || issues;
  const selectedIssue = displayIssues[selectedIdx] || displayIssues[0];
  const fixable = displayIssues.filter(i => i.tone !== 'success');

  // ── Manual save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!editText.trim()) { toast.warning('Nothing to save.'); return; }
    setSaving(true);
    try {
      let rid = activeRepairId;
      if (!rid) {
        // Create a repair record on-the-fly so we have something to save against
        const start = await issueRepairApi.startRepair(projectId, [selectedIssue || { text: 'manual edit', tone: 'warning' }]);
        const result = await issueRepairApi.pollJob(projectId, start.job_id);
        rid = result.repairs?.[0]?.id;
        if (rid) setActiveRepairId(rid);
      }
      if (rid) {
        await issueRepairApi.saveManual(rid, editText);
        toast.success('Chapter saved.');
        onRepairAccepted?.();
      } else {
        toast.error('Could not save — no repair record found.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [editText, activeRepairId, selectedIssue, projectId, onRepairAccepted]);

  // ── Save + Re-run (stays in modal, refreshes scores inline) ──────────────
  const handleSaveAndRerun = useCallback(async () => {
    if (!onRerunAnalysis) { toast.info('Re-run not available.'); return; }
    setRunningRerun(true);
    try {
      // 1. Save first if there's edited content
      if (editText.trim() && editText !== chapterContent) await handleSave();
      // 2. Re-run analysis
      setPrevScores(displayScores);
      toast.info('Re-running analysis…');
      const result = await onRerunAnalysis(initialChapterId);
      if (result) {
        setLiveScores({
          continuity: result.continuity,
          pacing:     result.pacing,
          plotholes:  result.plotholes,
          cliches:    result.cliches,
        });
        setLiveIssues((result.issues || []).map(it => ({
          ...it,
          chapter_id:     it.chapter_id     || initialChapterId,
          chapter_number: it.chapter_number,
          chapter_title:  it.chapter_title,
        })));
        toast.success('Analysis refreshed — scores updated below.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-run failed.');
    } finally {
      setRunningRerun(false);
    }
  }, [editText, chapterContent, displayScores, handleSave, initialChapterId, onRerunAnalysis]);

  // ── Claude fix ────────────────────────────────────────────────────────────
  const handleClaudeFix = useCallback(async () => {
    if (!displayIssues.length) return;
    setClaudeState('running');
    setPatchPreview(null);
    try {
      const issuePayload = claudeScope === 'chapter'
        ? displayIssues.filter(i => selectedIssue && (
            i.chapter_id === selectedIssue.chapter_id ||
            i.chapter_number === selectedIssue.chapter_number))
        : displayIssues;

      if (!issuePayload.length) {
        toast.warning('No linked issues to fix.');
        setClaudeState('idle');
        return;
      }
      const start = claudeScope === 'manuscript'
        ? await issueRepairApi.startBulkRepair(projectId, issuePayload)
        : await issueRepairApi.startRepair(projectId, issuePayload);

      toast.info('Claude is reviewing — this takes a moment…');
      const result = await issueRepairApi.pollJob(projectId, start.job_id);
      setRepairs(result.repairs || []);
      const first = (result.repairs || []).find(r => r.status === 'done');
      if (first) {
        setActiveRepairId(first.id);
        setPatchPreview(first.patched_content);
        setEditText(first.patched_content || '');
      }
      if (result.job?.status === 'failed') {
        setClaudeState('failed');
        toast.error('Claude repair failed. You can still edit manually.');
      } else {
        setClaudeState('done');
        toast.success(`Claude finished — ${result.repairs?.filter(r => r.status === 'done').length || 0} fix(es) ready.`);
      }
    } catch (err) {
      setClaudeState('failed');
      toast.error(err?.response?.data?.detail || err.message || 'Claude repair failed.');
    }
  }, [displayIssues, selectedIssue, claudeScope, projectId]);

  // ── Accept Claude patch ────────────────────────────────────────────────────
  const handleAcceptPatch = useCallback(async () => {
    if (!activeRepairId) return;
    setSaving(true);
    try {
      await issueRepairApi.acceptRepair(activeRepairId);
      toast.success('Chapter updated.');
      onRepairAccepted?.();
      setPatchPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Accept failed.');
    } finally {
      setSaving(false);
    }
  }, [activeRepairId, onRepairAccepted]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0"
        data-testid="issue-repair-modal"
      >
        {/* ── Header with scores ───────────────────────────────────────────── */}
        <DialogHeader className="shrink-0 border-b border-border">
          <div className="px-5 py-3">
            <DialogTitle className="text-[14px] font-semibold flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-primary" />
              Chapter Assessment
              {displayIssues.length > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  {fixable.length} issue{fixable.length !== 1 ? 's' : ''}
                </Badge>
              )}
              {runningRerun && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-normal ml-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> re-analysing…
                </span>
              )}
            </DialogTitle>
            {/* Score pills */}
            {displayScores && (
              <div className="flex items-center gap-5 px-1">
                <ScorePill label="Continuity" value={displayScores.continuity} prevValue={prevScores?.continuity} />
                <ScorePill label="Pacing"     value={displayScores.pacing}     prevValue={prevScores?.pacing} />
                <ScorePill label="Plotholes"  value={displayScores.plotholes}  prevValue={prevScores?.plotholes} suffix="" />
                <ScorePill label="Clichés"    value={displayScores.cliches}    prevValue={prevScores?.cliches} suffix="" />
                {liveScores && (
                  <span className="ml-auto text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Scores updated
                  </span>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {/* ── No issues state ──────────────────────────────────────────────── */}
        {displayIssues.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground px-6">
            <CheckCircle2 className="h-9 w-9 text-emerald-400 opacity-70" />
            <p className="text-[14px] font-medium text-foreground">No issues flagged</p>
            <p className="text-[12px] text-center max-w-xs">
              The chapter looks clean. You can still open the editor below to make manual adjustments.
            </p>
            <Button size="sm" variant="outline" className="gap-1.5 mt-1"
              onClick={() => setEditText(chapterContent)} data-testid="issue-repair-open-editor">
              <BookOpen className="h-3.5 w-3.5" /> Open Chapter Editor
            </Button>
          </div>
        )}

        {/* ── Two-col layout (issues + editor) ────────────────────────────── */}
        {displayIssues.length > 0 && (
          <div className="flex min-h-0 flex-1">
            {/* Left — issue list */}
            <div className="w-[210px] shrink-0 overflow-y-auto border-r border-border bg-card">
              {displayIssues.map((issue, idx) => (
                <IssueRow key={idx} issue={issue} active={idx === selectedIdx}
                  onClick={() => { setSelectedIdx(idx); setPatchPreview(null); setClaudeState('idle'); }} />
              ))}
            </div>

            {/* Right — editor pane */}
            <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
              {/* Selected issue banner */}
              {selectedIssue && (
                <div className={cn('flex items-start gap-2 rounded-md border px-3 py-2.5',
                  selectedIssue.tone === 'destructive'
                    ? 'border-rose-500/30 bg-rose-500/5'
                    : 'border-amber-500/30 bg-amber-500/5')}>
                  <AlertTriangle className={cn('mt-0.5 h-3.5 w-3.5 shrink-0',
                    selectedIssue.tone === 'destructive' ? 'text-rose-400' : 'text-amber-400')} />
                  <div>
                    <p className="text-[13px] font-medium text-foreground">{selectedIssue.text}</p>
                    {(selectedIssue.chapter_title || selectedIssue.chapter_number) && (
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {selectedIssue.chapter_title || `Chapter ${selectedIssue.chapter_number}`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Claude scope + trigger */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <p className="text-[10.5px] font-medium text-muted-foreground uppercase tracking-wider">Claude Auto-Fix Scope</p>
                  <ScopeToggle value={claudeScope} onChange={setClaudeScope} />
                </div>
                <Button size="sm" onClick={handleClaudeFix}
                  disabled={claudeState === 'running'} className="gap-1.5 shrink-0"
                  data-testid="issue-repair-claude-btn">
                  {claudeState === 'running'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Sparkles className="h-3.5 w-3.5" />}
                  {claudeState === 'running' ? 'Claude is fixing…' : claudeState === 'done' ? 'Re-run Claude' : 'Claude Fix'}
                </Button>
              </div>

              {/* Patch preview */}
              {patchPreview && (
                <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                  <p className="mb-1.5 text-[11.5px] font-semibold text-primary">
                    Claude's suggestion is loaded — review, edit, then Save.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="default" className="h-6 gap-1 text-[11px]"
                      onClick={handleAcceptPatch} disabled={saving} data-testid="issue-repair-accept-btn">
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                      Accept as-is
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[11px]"
                      onClick={() => setPatchPreview(null)}>Dismiss</Button>
                  </div>
                </div>
              )}

              {/* Text editor */}
              <div className="flex flex-col gap-1">
                <p className="text-[10.5px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <BookOpen className="h-3 w-3" /> Chapter Text — Edit Directly
                </p>
                <Textarea
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  className="min-h-[200px] resize-y font-mono text-[12.5px] leading-relaxed"
                  placeholder="Chapter content will appear here after Claude Fix, or paste it in manually…"
                  data-testid="issue-repair-editor"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Action footer ────────────────────────────────────────────────── */}
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={handleSave} disabled={saving || !editText.trim()}
              data-testid="issue-repair-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
            <Button size="sm" variant="default" className="gap-1.5"
              onClick={handleSaveAndRerun} disabled={runningRerun || saving}
              data-testid="issue-repair-rerun-btn">
              {runningRerun ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Save &amp; Re-run Analysis
            </Button>
          </div>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}
            className="text-muted-foreground">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
